// Shared visual language + label maps for the Work Order Management System.
// Visual reference: TFS CRM (navy sidebar, lime accent, white workspace).

export const COLORS = {
  navy: '#0E2A47',
  navyDark: '#0A2038',
  navyMuted: '#16324F',
  lime: '#A3D65C',
  limeDark: '#8FC93A',
  actionBg: '#FBF3E4',
  actionBorder: '#E8C99B',
  actionText: '#C8641E',
  pageBg: '#F5F6F8',
};

// Friendly display labels — never expose raw backend role identifiers in the UI.
export const ROLE_LABELS = {
  owner: 'Global Administrator',
  admin: 'General Administrator',
  manager: 'Operations Manager',
  field_user: 'Field Personnel',
  tfs_operations: 'Field/Ops Supervisor',
  client_facilities_director: 'Facilities Director',
  client_location_requestor: 'Location Requester',
};

export const roleLabel = (role) => ROLE_LABELS[role] || 'User';

// Requestor attribution. When a request was entered internally by TFS on behalf
// of a customer (entered_by_tfs), show "[TFS User] – TFS on behalf of [Customer]".
// Direct client submissions show the user's name unchanged.
export const formatRequestor = (name, customerName, enteredByTfs) => {
  if (!name) return '—';
  if (enteredByTfs) return `${name} – TFS on behalf of ${customerName || 'customer'}`;
  return name;
};

export const SR_STATUS = {
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved - Awaiting TFS Work Order Processing',
  DECLINED: 'Declined',
  INFO: 'Information Requested',
  HOLD: 'On Hold',
};

export const statusStyle = (status) => {
  switch (status) {
    case SR_STATUS.SUBMITTED:
      return { className: 'bg-amber-100 text-amber-800 border-amber-200', short: 'Submitted' };
    case SR_STATUS.APPROVED:
      return { className: 'bg-green-100 text-green-800 border-green-200', short: 'Approved – Awaiting WO' };
    case SR_STATUS.DECLINED:
      return { className: 'bg-red-100 text-red-700 border-red-200', short: 'Declined' };
    case SR_STATUS.INFO:
      return { className: 'bg-blue-100 text-blue-700 border-blue-200', short: 'Info Requested' };
    case SR_STATUS.HOLD:
      return { className: 'bg-slate-200 text-slate-700 border-slate-300', short: 'On Hold' };
    default:
      return { className: 'bg-slate-100 text-slate-600 border-slate-200', short: status };
  }
};

export const urgencyLabel = (t) => {
  if (!t) return 'Standard';
  const s = String(t).toLowerCase();
  if (s.includes('emergency')) return 'Emergency';
  if (s.includes('urgent')) return 'Urgent';
  if (s.includes('standard')) return 'Standard';
  return t;
};

// Canonical request-origin classification → display badge.
export const SUBMISSION_SOURCE = {
  LOCATION_REQUESTER: 'Location Requester',
  FD_DIRECT: 'Facilities Director — Direct to TFS',
};

export const submissionStyle = (source) => {
  if (source === SUBMISSION_SOURCE.FD_DIRECT) {
    return { label: 'Director — Direct to TFS', className: 'bg-[#0E2A47] text-white border-[#0E2A47]' };
  }
  return { label: 'Location Requester', className: 'bg-[#8FC93A]/15 text-[#3f6d12] border-[#8FC93A]/40' };
};

export const typeStyle = (type) =>
  (type === 'Emergency' || type === 'Urgent')
    ? 'bg-red-100 text-red-700 border-red-200'
    : 'bg-slate-100 text-slate-600 border-slate-200';

// Human-readable descriptions for immutable activity actions.
export const ACTION_LABELS = {
  submitted: 'Request submitted',
  edited: 'Request edited',
  approval_requested: 'Approval requested',
  approved: 'Approved',
  declined: 'Declined',
  information_requested: 'Information requested',
  hold: 'Placed on hold',
  status_changed: 'Status changed',
  converted: 'Converted to Work Order',
  work_order_created: 'Work Order created',
  priority_assigned: 'Priority assigned',
  priority_changed: 'Priority changed',
  assignment_created: 'Assigned',
  assignment_changed: 'Assignment changed',
  scheduled: 'Scheduled',
  dispatched: 'Dispatched',
  accepted: 'Assignment accepted',
  en_route: 'En route',
  arrived: 'Arrived on site',
  work_started: 'Work started',
  photos_uploaded: 'Photos uploaded',
  notes_entered: 'Documentation updated',
  authorization_requested: 'Authorization requested',
  work_completed: 'Service visit completed',
  follow_up_required: 'Follow-up required',
  signature_captured: 'Signature captured',
  service_report_submitted: 'Field Service Report submitted',
  awaiting_closeout: 'Awaiting Administrative Closeout',
  administrative_closeout: 'Administrative closeout — Completed',
  direct_to_tfs: 'Submitted directly to TFS',
  draft_saved: 'Saved as draft',
  routed: 'Recorded & routed',
  cancelled: 'Cancelled',
};